import type { InterviewInviteDetailDto } from '@sara/api-client';

export function fullName(invite: InterviewInviteDetailDto): string {
  return `${invite.candidate.firstName} ${invite.candidate.lastName}`.trim() || 'Candidate';
}

export function initials(invite: InterviewInviteDetailDto): string {
  const f = invite.candidate.firstName?.[0] ?? '';
  const l = invite.candidate.lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '··';
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDuration(startIso: string, endIso: string | null | undefined): string {
  if (!endIso) return '—';
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (ms < 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export function formatDurationSecs(secs: number | null | undefined): string {
  if (secs === null || secs === undefined || secs < 0) return '—';
  const total = Math.floor(secs);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export interface InviteValidity {
  /** Short label, e.g. "Valid until Mar 27, 2026" or "Expired Mar 27, 2026". */
  label: string;
  expired: boolean;
  /** True when the invite carries no expiry at all. */
  perpetual: boolean;
}

export function inviteValidity(
  expiresAt: string | null | undefined,
  now: Date = new Date()
): InviteValidity {
  if (!expiresAt) return { label: 'No expiry', expired: false, perpetual: true };
  const expired = new Date(expiresAt).getTime() < now.getTime();
  return {
    label: expired ? `Expired ${formatDate(expiresAt)}` : `Valid until ${formatDate(expiresAt)}`,
    expired,
    perpetual: false,
  };
}

export function formatTurnOffset(
  turnStartedAtUnix: number | null | undefined,
  sessionStartedAtIso: string | null | undefined
): string | null {
  if (typeof turnStartedAtUnix !== 'number' || !Number.isFinite(turnStartedAtUnix)) return null;
  if (!sessionStartedAtIso) return null;
  const sessionStartUnix = new Date(sessionStartedAtIso).getTime() / 1000;
  if (!Number.isFinite(sessionStartUnix)) return null;
  const offsetSeconds = turnStartedAtUnix - sessionStartUnix;
  if (offsetSeconds < 0) return null;
  const total = Math.floor(offsetSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${ss}`;
  return `${minutes}:${ss}`;
}

export function formatEndReason(reason: string): string {
  return reason
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export function camelToTitle(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function isRatingObject(value: unknown): value is { rating: string; summary: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'rating' in value &&
    'summary' in value &&
    typeof (value as Record<string, unknown>).rating === 'string' &&
    typeof (value as Record<string, unknown>).summary === 'string'
  );
}

export type RatingTone = 'strong' | 'adequate' | 'weak' | 'neutral';

export const TONE_RING: Record<RatingTone, string> = {
  strong: 'bg-success',
  adequate: 'bg-warning',
  weak: 'bg-destructive',
  neutral: 'bg-muted-foreground/40',
};

export const TONE_FILL: Record<RatingTone, string> = {
  strong: 'bg-success/15 text-success ring-success/25',
  adequate: 'bg-warning/15 text-warning ring-warning/25',
  weak: 'bg-destructive/10 text-destructive ring-destructive/25',
  neutral: 'bg-muted text-muted-foreground ring-border',
};

export const RATING_BARS: Record<RatingTone, number> = {
  strong: 3,
  adequate: 2,
  weak: 1,
  neutral: 0,
};

export function ratingTone(rating: string): RatingTone {
  const r = rating.toLowerCase();
  if (r === 'strong') return 'strong';
  if (r === 'adequate') return 'adequate';
  if (r === 'weak') return 'weak';
  return 'neutral';
}

/** Maps a numeric rubric score (1–5 scale) to a RatingTone. */
export function scoreTone(score: number): RatingTone {
  if (score >= 4) return 'strong';
  if (score >= 3) return 'adequate';
  return 'weak';
}
